/**
 * Full-screen ("presenting") view of the org board, made for the wall TV in
 * Flash's office (75" 4K, Nov 2026): the board fills the screen with no app
 * chrome, scaled so every division fits across.
 */

export const BOARD_ZOOM_MIN = 0.15;
export const BOARD_ZOOM_MAX = 2.5;

/**
 * The zoom that fits the whole board WIDTH into the visible width. The board is
 * scaled with `transform: scale(zoom)` from its top-left corner, so its painted
 * width is `treeWidth * zoom` plus the tree's own padding (unscaled).
 */
export function fitBoardZoom(visibleWidth: number, treeWidth: number, paddingPx = 0): number {
    if (!(visibleWidth > 0) || !(treeWidth > 0)) return 1;
    const zoom = (visibleWidth - paddingPx * 2) / treeWidth;
    if (!isFinite(zoom) || zoom <= 0) return 1;
    // Round DOWN to a hundredth, so rounding never pushes the last division off-screen.
    const rounded = Math.floor(zoom * 100) / 100;
    return Math.min(BOARD_ZOOM_MAX, Math.max(BOARD_ZOOM_MIN, rounded));
}

/** One zoom-button press changes the zoom by this factor (5%). */
export const BOARD_ZOOM_STEP_FACTOR = 1.05;

/**
 * The next zoom after `steps` presses (negative = out; fractions for wheel
 * ticks). Multiplicative, so a step feels the same at 0.27 as at 1.5 (the old
 * fixed +-0.15 was a 55% jump in the full-screen view). Kept to thousandths —
 * hundredths were already a 4% jump at the full-screen zoom — and always moving
 * at least a thousandth so small steps never stall.
 */
export function stepBoardZoom(zoom: number, steps: number): number {
    if (!isFinite(zoom) || zoom <= 0) return 1;
    const round = (z: number) => Math.round(z * 1000) / 1000;
    let next = round(zoom * Math.pow(BOARD_ZOOM_STEP_FACTOR, steps));
    if (steps !== 0 && next === round(zoom)) next = round(next + (steps > 0 ? 0.001 : -0.001));
    return Math.min(BOARD_ZOOM_MAX, Math.max(BOARD_ZOOM_MIN, next));
}
